'use client'
import { PortableTextProps } from '@portabletext/react'
import { SanityReference } from '@sanity/image-url/lib/types/types'
import { AspectRatio, AtLinkTarget, Currency, Media, OrTextBlockContentProps, SpaceType } from '@studio/atomic-ui'
import { ProductSource, ProductType } from '../../types/product'

/** PDP template doesn't actually exist as a template in Sanity, rather, pgPage->product is used */
export type SanityTemplate =
  | 'tmFlexible'
  | 'tmVisitorGuide'
  | 'tmContact'
  | 'tmContactV2'
  | 'tmCheckout'
  | 'tmPdp'
  | 'tmGuestList'
  | 'tmBuildingDetail'
  | 'tmProductListing'
  | null

export type SanityInitialPageQuery = {
  template: SanityTemplate
  title: string
  product: { source: string; platformId: number; productName: string } | null
  seo: SanitySeo
  templateId?: string
  abTestingConfiguration?: {
    active: boolean
    cookieName: string
    pageVariants: Array<{
      controlVariant: boolean
      variantName: string
      trafficAllocation: number
      variantId?: string
      title?: string
      seo?: SanitySeo
    }>
  }
} | null

export type SanityRedirectSlug = {
  from: string
  to: string
} | null

export type SanitySeo = Partial<{
  metaTitle: string
  metaDescription: string
  metaKeywords: string
  metaAuthor: string
  metaRobots: string
  ogTitle: string
  ogUrl: string
  ogImage: string
}>

export type SanitySvgAsset = {
  alt: string | null
  src: string
}

export type SanityAtImage = {
  alt: string
  caption: string | null
  credit: string | null
  asset: SanityReference
  src: string
  dimensions?: { width?: number; height?: number }
}

export type SanityOrSpaces = {
  type: 'orSpaces'
  name: string
  title: string
  spaceType?: SpaceType
  hideSpaceTypeFilter?: boolean
  region?: string
  building?: string
  disableRegionFilter: boolean
  disableBuildingFilter: boolean
}

export type SanityAtLink = {
  variant: 'default' | 'inline' | 'secondary'
  href: string
  label: string
  ariaLabel?: string | null
  actionType?: 'openUrl' | 'none' | null
  actionValue?: string | null
  hasIcon: boolean
  leftIcon?: SanitySvgAsset | null
  rightIcon?: SanitySvgAsset | null
  target?: AtLinkTarget | null
}

export type SanityAtButton = {
  label: string
  variant: 'primary' | 'secondary' | 'tertiary'
  hasIcon: boolean
  leftIcon: SanitySvgAsset | null
  rightIcon: SanitySvgAsset | null
  target: '_blank' | '_self' | null
  actionType: 'openUrl' | 'none' | null
  actionValue: string | null
}

export type SanityAtIconInformative = {
  title: string
  subtitle: string
  body: string
  icon: SanitySvgAsset | null
}

export type SanityAtFormField = {
  inputType: 'text' | 'checkbox' | 'radio' | 'select' | 'grid' | 'divider' | 'text-area' | 'calendar' | 'label'
  collapsible?: boolean
  defaultExpanded?: boolean
  labelClassName?: string
  dividerVariant?: 'default' | 'compact'
  calendarSubCheckboxFieldName?: string
  calendarSubCheckboxLabel?: string
  grid?: SanityAtFormField[]
  fieldName?: string
  label?: string
  placeholder?: string
  helperText?: PortableTextProps['value']
  errorText?: PortableTextProps['value']
  icon?: SanitySvgAsset
  validator?: 'phone' | 'email' | 'none'
  showCharacterLimit?: boolean
  isRequired?: boolean
  maxLength?: number
  minLength?: number
  options?: {
    Label: string
    Value: string
  }[]
}

export type SanityMlNotification = {
  title: string
  message: PortableTextProps['value']
  linkLabel: string | null
  notificationType: 'error' | 'success' | 'warning'
}

export type SanityMlSnackbar = {
  content: string
  buttonLabel: string
  status: 'positive' | 'error' | 'warning' | 'informative'
}

export type SanityMlFormSuccess = {
  _id: string
  name: string
  heading: string
  subheading?: string | null
  ctaLabel?: string | null
  ctaHref?: string | null
}

export type SanityOrTab = {
  label: string
  icon?: SanitySvgAsset
  content: SanityAtFormField[] | SanityOrFlexibleBlock[] | SanitySmProduct[]
}

export type SanityOrTabs = {
  name: string
  tabsTitle: string | null
  fieldName: string | null
  tabs: SanityOrTab[]
}

export type SanityOrForm = {
  id: string
  name: string
  type: 'form'
  title?: PortableTextProps['value']
  formId?: string
  submitButton?: SanityAtButton
  submissionType?: string
  fields: (SanityOrTabs | SanityAtFormField)[]
  isGate?: boolean
  gateRevealsContentAfter?: boolean
  gateAttachedFileUrl?: string
  sendGatedContentByEmail?: boolean
  successSnackbar?: SanityMlSnackbar | null
  serverErrorNotification?: SanityMlNotification | null
  singleFieldErrorNotification?: SanityMlNotification | null
  multipleFieldsErrorNotification?: SanityMlNotification | null
}

export type SanityOrFlexibleBlock = {
  contentTitle: string
  layout: 'flexibleA' | 'flexibleB' | 'flexibleC' | 'flexibleD' | 'cardA'
  description: string | null
  details: SanityAtIconInformative[]
  detailsBody: string | null
  detailsTitle: string | null
  images: SanityAtImage[]
  address: string | null
  shortDescription: string | null
  directions: string[] | null
  cards: any | null
  aspectRatio: AspectRatio | null
}

export enum SanityOrBaseContentLayout {
  descriptions = 'descriptions', // Content A
  longText = 'longText',
  finalAddendum = 'finalAddendum', // Content C
  paragraphBodyD = 'paragraphBody-d', // content D
  featureList = 'featureList',
  paragraphBody = 'paragraphBody', // Content F
  fetureListWithColumns = 'fetureListWithColumns',
  cards = 'cards',
  simpleTextList = 'simpleTextList',
  cardsOnly = 'cardsOnly',
  zoSection = 'zo',
}

export type SanitySplitTypes = 'full' | 'top-half' | 'bottom-half'
export type SanityOrientationTypes = 'normal' | 'reverse'
export type SanityAlignmentTypes = 'left' | 'center'
export type SanityBackgroundTypes = 'gray' | 'none'
// TODO: Create mappings for other layouts (see query for currently mapped layouts)
export type SanityOrBaseContent = {
  type: 'orBaseContent'
  layout: SanityOrBaseContentLayout
  outline: string | null
  outline2: string | null
  logo: SanityAtImage | null
  sectionTitle: string | null
  sectionTitle2: string | null
  sectionSubtitle: string | null
  sectionDescription: string | null
  sectionDescription2: string | null
  sectionFooter: string | null
  images: SanityAtImage[] | null
  media: Media[] | null
  sectionBody: PortableTextProps['value'] | SanityAtImage[] | null
  handwrittenDetail: string | null
  list: Array<SanityOrListItem> | null
  cards: any[] | null
  button: SanityAtButton | null
  splitContent: SanitySplitTypes
  orientation: SanityOrientationTypes
  alignment: SanityAlignmentTypes
  background: SanityBackgroundTypes
  ctaBar?: SanityMlCtaBarProps | null
  videoABTestingConfig?: VideoFileAbTestingConfig | null
}

export type SanityOrListItem = {
  subtitle: string | null
  title: string
  body: string | null
  link: SanityAtLink | null
  icon: SanitySvgAsset | null
}

export type SanityMlNavbarDropdown = {
  title: string
  dropdownLinks: SanityAtLink[] | null
}

export type TemplateVariant = {
  controlVariant: boolean
  variantName: string
  trafficAllocation: number
  content?: {
    _type?: string
    [key: string]: any
  }
  title?: string
  header?: SanityPage<unknown>['header']
  footer?: SanityPage<unknown>['footer']
  seo?: SanitySeo
  variantId?: string
}

export type AbTestingConfiguration = {
  active: boolean
  pageVariants: TemplateVariant[]
  cookieName: string
  pageTemplateId?: string
  effectiveTemplateType?: string
}

export type ModuleVariant = {
  controlVariant: boolean
  variantName: string
  trafficAllocation: number
  content?: {
    _type?: string
    [key: string]: any
  }
  variantId?: string
}

export type ModuleABTesting = {
  type: 'navigation' | 'footer'
  cookieName: string
  variants: ModuleVariant[]
}

export type ModuleABTestingConfiguration = {
  active: boolean
  moduleVariants: ModuleABTesting[]
}

export type SelectedModuleVariants = {
  selectedNavigationVariant?: string
  selectedNavigationVariantId?: string
  navigationCookieName?: string
  selectedFooterVariant?: string
  selectedFooterVariantId?: string
  footerCookieName?: string
}

export type SpaceCardAbTestingConfiguration = {
  active: boolean
  spaceVariants: SpaceVariant[]
  cookieName: string
  variantNameSelected?: string
}

export type SanitySmVideoFile = {
  _id: string
  type: 'video'
  name?: string
  src: string
  poster?: string
  loop?: boolean
  autoPlay?: boolean
  videoControls?: string[]
}

export type VideoFileVariant = {
  variantName: string
  videoFile: SanitySmVideoFile
  trafficAllocation: number
}

export type VideoFileAbTestingConfig = {
  active: boolean
  cookieName: string
  variants: VideoFileVariant[]
}

export type SpaceVariant = {
  trafficAllocation: number
  variantName: string
  spaceDetails: SpaceDetails[]
}

export type SpaceDetails = {
  key: string
  inquiryOnly: boolean
  inquiryPrice: number
  priceRate: number
  productName: string
  rateType: string
  textAfterPrice: string
  specialProductDetails: specialProductDetails[]
}

export type SanityPage<T> = {
  title: string
  pageType?: string
  id?: string
  abTestingConfiguration?: AbTestingConfiguration
  moduleABTestingConfiguration?: SelectedModuleVariants
  header?: {
    logo: SanitySvgAsset
    navigationLinks?: (SanityMlNavbarDropdown | SanityAtLink)[] | null
    ctaButtons?: SanityAtButton[] | null
    alternateLink?: SanityAtLink
  }
  footer?: {
    copyright: string
    logo: SanitySvgAsset
    contact: PortableTextProps['value']
    secondaryLinks: SanityAtLink[]
    socialMedias: SanityAtLink[] | null
    links: SanityAtLink[]
  }
  template: T | null
}

export type SanityTmContact = {
  description: string | null
  contactImage: SanityAtImage | null
  contactInfo: PortableTextProps['value']
  contactForm: SanityOrForm | null
  snackbarStatus: SanityMlSnackbar | null
  serverErrorNotification: SanityMlNotification | null
  singleFieldErrorNotification: SanityMlNotification | null
  multipleFieldsErrorNotification: SanityMlNotification | null
}

export type SanityContactV2Tab = {
  tabKey: 'office' | 'event' | 'general'
  tabLabel: string
  tabDescription?: string | null
  tabIcon?: SanitySvgAsset
  form: SanityOrForm | null
}

export type SanityTmContactV2 = {
  title: string
  description?: string | null
  submitButtonLabel: string
  stickySubmitButton?: boolean | null
  inquiryTabs: SanityContactV2Tab[]
  formSuccess?: SanityMlFormSuccess | null
  successNotification: SanityMlSnackbar | null
  serverErrorNotification: SanityMlNotification | null
  singleFieldErrorNotification: SanityMlNotification | null
  multipleFieldsErrorNotification: SanityMlNotification | null
}

export type SanityTmCheckout = {
  title: string
  steps: Array<{
    id: string
    name: string
    title: PortableTextProps['value']
    fields: SanityAtFormField[]
    submitButton: SanityAtButton
  }>
  confirmation: {
    icon: SanitySvgAsset
    heading: PortableTextProps['value']
    label: string
    guestRegistration: PortableTextProps['value']
    reservationDetails: PortableTextProps['value']
    modifyInstructions: PortableTextProps['value']
    redirectLink: SanityAtLink
  }
  redirectButton: SanityAtLink
  serverErrorNotification: SanityMlNotification | null
  singleFieldErrorNotification: SanityMlNotification | null
  multipleFieldsErrorNotification: SanityMlNotification | null
  bookingUnavailableError: SanityMlNotification | null
  declinedCreditCardError: SanityMlNotification | null
}

/** Not a real content type, placeholder for the other solutions section in PDP. Link gets programmatically created at /spaces?type=spaceType */
export type SmWorkspaceCard = Pick<SanitySmProduct, 'images' | 'spaceType'>

export type SanitySmProduct = {
  _id: string
  plpRank?: number | null
  capacity: number
  stripePrice: number
  prodStripePrice: number
  spaceType: ProductType
  slug: string
  productName: string
  bookableDays: number
  building: string
  source: ProductSource
  platformId: string
  description: PortableTextProps['value']
  highlights: SanityAtIconInformative[] | null
  spaceCapabilities: SanityOrFlexibleBlock | null
  /** PDP template requires that this is a simple text list */
  additionalServices: SanityOrBaseContent | null
  images: SanityAtImage[]
  media: Media[]
  floorplanImage: SanityAtImage | null
  inquiryOnly: boolean
  location: SanityOrTabs | null
  similarSpaces: SanityOrTabs | null
  textBeforePrice?: string
  inquiryCurrency?: Currency
  inquiryPrice?: number
  rateType?: 'hour' | 'day' | 'week' | 'month'
  textAfterPrice?: string
  textAfterPricePdp?: string
  /** Using product for mapping `spaceType` and `images`. This is technically a link to a PLP with `spaceType` filter pre-applied */
  otherSolutions: SmWorkspaceCard[] | null
  inquireSuccessMessage: SanityMlSnackbar | null
  specialProductDetail: {
    detail: string | null
    icon: SanitySvgAsset | null
  } | null
  specialProductDetails: specialProductDetails[]
  failureMessage: SanityMlSnackbar | null
  brandMoment: SanityOrBaseContent | null
  buildingBusinessHours?: SanityBusinessHours
  spaceBusinessHours?: SanityBusinessHours
  earliestBookableHours?: number
  hideCapacityOnPdp?: boolean
  squareFootage?: number
  hideSquareFootageOnPdp?: boolean
  minimumBookingDuration: SanityMinimumBookingDuration
  spaceABTestingConfig: SpaceCardAbTestingConfiguration | null
  priceRate?: number
}
export type SanityBusinessHours = {
  startHour: string
  endHour: string
}

export type specialProductDetails = {
  detail: string | null
  icon: SanitySvgAsset | null
}
export type SanityMinimumBookingDuration = {
  title: string | undefined
  value: string | undefined
}

export type SanityTmProductListing = {
  notFoundText: string | null
  notFoundTitle: string | null
  contactDetails: SanityAtIconInformative[] | null
  loadButtonLabel: string | null
  notFindingCard: {
    contactButton: SanityAtButton | null
    notFindingImage: SanityAtImage | null
    notFindingTitle: string | null
  }
}

export type SanityBuilding = SanityNonBookableDates & {
  isNonStudioLocation: boolean
  nonStudioLocationExternalLink?: SanityAtLink
}

export type SanitySmTextBlock = {
  type: 'textBlock'
  key: string
  name: string
  content: OrTextBlockContentProps
}

export type SanityOrContainerBlock =
  | SanitySmTextBlock
  | SanityMlCtaBarProps
  | SanityOrForm
  | SanityMlSiteBanner
  | SanityOrMap
  | SanityOrCarousel
  | SanityOrBaseContent
  | SanityOrSpaces

export enum SanityOrCarouselType {
  Building = 'building',
  Product = 'product',
  CustomCard = 'customCard',
}

type SanityOrCarouselContent =
  | { carouselType: SanityOrCarouselType.Building; buildings: SanityBuildingCard[] }
  | { carouselType: SanityOrCarouselType.Product; products: SanitySmProduct[] }
  | { carouselType: SanityOrCarouselType.CustomCard; customCards: SanityCustomCard[] }

export type SanityOrCarousel = {
  type: 'carousel'
  title?: string
} & SanityOrCarouselContent

export type SanityOrContainer = {
  type: 'orContainer'
  containerTitle?: string
  description?: string
  blocks: Array<SanityOrContainerBlock>
}

export type SanityOrContainerTwoCol = {
  type: 'orContainerTwoCol'
  columnRatio: string
  blocks: Array<SanityOrContainerBlock>
}

export type SanityTmFlexible = {
  id: string
  title: string
  hideTitle?: boolean
  /**
   * blocks: is an array of one or multiple {SanityOrContainer}
   */
  blocks: Array<SanityOrContainer | SanityOrContainerTwoCol | SanityOrBaseContent>
}

export interface SanityMlCtaBarProps {
  type: 'ctaBar'
  label: string
  rightIcon?: { src: string; alt: string }
  actionValue?: string
  target?: '_blank' | '_self'
  secondaryLabel?: string
  secondaryRightIcon?: { src: string; alt: string }
  secondaryActionValue?: string
  secondaryTarget?: '_blank' | '_self'
}

enum MlSiteBannerVariant {
  LOGO = 'logo',
  HEADING_CTA = 'heading-cta',
  SIDE_IMG_HEADING_CTA = 'side-img-heading-cta',
  HOME_SEARCH = 'home-search',
}

export type SanityHomeSearchSolution = {
  solutionKey: string
  label: string
  description: string
  buttonLabel: string
}

export type SanityMlSiteBanner = {
  type: 'mlSiteBanner'
  name: string
  variant: MlSiteBannerVariant
  headline: string
  description: string
  image: SanityAtImage
  media: Media[]
  mediaDesktop?: Media[]
  logo: SanityAtImage
  callToAction: SanityAtLink
  orientation: SanityOrientationTypes
  alignment: SanityAlignmentTypes
  defaultSolutionKey?: string
  solutions?: SanityHomeSearchSolution[]
}

export type LatLng = {
  lat: number
  lng: number
}

export type SanityOrList = {
  name: string
  items: Array<SanityOrListItem>
}

export type SanityOrMapTile = {
  name: string
  location: LatLng
  address: string
  details: { name: string; items: Array<{ icon: SanitySvgAsset; title: string }> }
  building: Pick<SanityTmBuildingDetail, 'name' | 'buildingImages' | 'address' | 'spacesSection'> & {
    page?: {
      url?: string
      name?: string
    }
  }
}

export type SanityOrMap = {
  type: 'orMap'
  title?: string
  description?: string
  Name: string
  Locations: Array<SanityOrMapTile>
}

export type SanityTmBuildingDetail = {
  name: string
  buildingImages: Array<SanityAtImage>
  address: {
    heading: SanitySmTextBlock
    addressInfo: PortableTextProps['value']
    directionLink?: SanityAtLink
    mapTile: SanityOrMapTile
  }
  pageLinks: Array<SanityAtLink>
  content: Array<SanityOrBaseContent | SanityOrTabs>
  spacesSection: {
    spaceType: string
    allSpacesButton: SanityAtButton
    spaceRates: { spaceDescription: string; rate: string }
  }
  identifiers: {
    building: string
  }
}

export type SanityBuildingCard = {
  building: string
  page?: {
    name?: string
    url?: string
  }
  buildingImages: SanityAtImage[]
  address: PortableTextProps['value']
  allSpacesButton: SanityAtButton
  spaceRates: { spaceDescription: string; rate: string }[]
}

export type SanityCustomCard = {
  name: string
  title: string
  description: string
  image: SanityAtImage
  ctaBar?: SanityMlCtaBarProps
}

export type SanityNonBookableDates =
  | {
      nonBookableDates?: {
        name: string
        dates: {
          startDate: string
          endDate?: string
        }[]
      } | null
    }
  | undefined

export type SanitySmBuilding = {
  slug?: string | null
  name?: string | null
  displayName?: string | null
  active?: boolean | null
  cloudgateEnabled?: boolean | null
  contactEmail?: string | null
  eventsContactEmail?: string | null
  prodYardikubeLocationId?: string | null
  sandboxYardikubeLocationId?: string | null
  prodTripleseatLocationId?: string | null
  sandboxTripleseatLocationId?: string | null
  city?: {
    name?: string | null
    slug?: string | null
    timezone?: string | null
    currency?: string | null
    taxPercentage?: number | null
  } | null
}
